import { FormPanel } from '@checkit/react-components';
import { CancelIcon } from '@checkit/react-components/build/components/Icons';
import {
  Button,
  makeStyles,
  Theme,
  Toolbar
} from '@material-ui/core';
import clsx from 'clsx';
import { History } from 'history';
import React, {
  ChangeEvent,
  createRef,
  FormEvent,
  RefObject,
  useEffect,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import {
  TextValidator,
  ValidatorForm
} from 'react-material-ui-form-validator';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import useWindowUnload from '../../checkpoints/components/form/hooks/usePreventWindowUnload';
import { SaveIcon } from '../../common/SaveIcon';
import MaterialUiLink from '../../components/MaterialUiLink';
import RouteLeavingGuard from '../../components/routeLeaving/routeLeaving';
import { State } from '../../state';
import { appMessages } from '../../translations';
import { UserDefinedRoleCreateDto } from '../state';
import { messages } from './translations';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    margin: theme.spacing(1)
  },
  formInput: {
    marginTop: theme.spacing(0)
  },
  iconSmall: {
    fontSize: 20
  },
  leftIcon: {
    marginRight: theme.spacing(1)
  },
  toolBar: {
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center'
    },
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3)
  }
}));

const MAX_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;

export const UserDefinedRoleForm = connect(
  ({ userDefinedRoles }: State) => ({ userDefinedRoles })
)(function UserDefinedRoleFormImpl({
  code,
  history,
  userDefinedRoles,
  handleSubmit
}: {
  /** The code of the existing user-defined role to edit (or `null` if a new user-defined role is being created). */
  code: string | null,
  /** Used to navigate back to the user-defined listing page once a user-defined role has been created/updated. */
  history: History,
  /** All fetched user-defined roles, from global redux state. */
  userDefinedRoles: State['userDefinedRoles'],
  /** A callback which handles saving or updating the user-defined roles with updated details. */
  handleSubmit: (data: UserDefinedRoleCreateDto) => Promise<AnyAction>
}) {

  const { formatMessage } = useIntl();
  const classes = useStyles();
  const formRef = createRef() as RefObject<ValidatorForm>;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dirty, setDirty] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  useWindowUnload(dirty);

  useEffect(() => {
    if (code !== null) {
      const userDefinedRole = userDefinedRoles.byCode[code];
      if (userDefinedRole) {
        setName(userDefinedRole.name);
        setDescription(userDefinedRole.description);
      }
    }
  }, [userDefinedRoles, code]);

  const onFormChange = (_: ChangeEvent<HTMLInputElement>) => {
    if (code === null) {
      setDirty(true);
    }
  };

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onChangeDescription = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);

    // Disable save button when description is same as when page loaded.
    if (code !== null) {
      const userDefinedRole = userDefinedRoles.byCode[code];
      if (userDefinedRole && userDefinedRole.description === event.target.value ) {
        setIsFormSubmitting(false);
        setDirty(false);
      } else {
        setDirty(true);
      }
    }
  };

  /** Submits the current form, including validating it first. */
  async function submit( ) {
    const form = formRef.current;
    if (form == null) {
      throw new Error('Form submitted before being initialized');
    }

    const isValid = await form.isFormValid(false);
    if (!isValid) {
      return;
    }

    setIsFormSubmitting(true);
    setDirty(false);

    await handleSubmit({ name: name.trim(), description })
      .then((resultingAction) => {
        if (resultingAction.type.indexOf('_SUCCESS') !== -1) {
          history.push('/user-defined-roles');
          setIsFormSubmitting(false);
        }
      })
      // let the user edit the user-defined role again and re-try
      .catch(( ) => setIsFormSubmitting(false));
  }

  // Event Handlers
  function onFormSubmit(event: FormEvent) {
    event.preventDefault( );
    submit( );
    setIsFormSubmitting(true);
  }

  return (
    <>
      <ValidatorForm ref={formRef} onSubmit={onFormSubmit} onChange={onFormChange}>
        <FormPanel
          heading={formatMessage(messages['userDefinedRole.form.heading'])}
          name="user-defined-role"
          initialExpanded={true}
        >
          <TextValidator
            fullWidth={true}
            id="name"
            className={classes.formInput}
            label={formatMessage(messages['userDefinedRole.form.name'])}
            name="name"
            autoFocus={true}
            disabled={code ? true : false}
            value={name}
            withRequiredValidator={true}
            validators={['required', 'trim', `maxStringLength:${MAX_NAME_LENGTH}`]}
            errorMessages={[
              formatMessage(
                appMessages['app.required'],
                { fieldName: formatMessage(messages['userDefinedRole.form.name']) }
              ),
              formatMessage(
                appMessages['app.nonBlank'],
                { fieldName: formatMessage(messages['userDefinedRole.form.name']) }
              ),
              formatMessage(
                appMessages['app.maxLength'],
                {
                  fieldName: formatMessage(messages['userDefinedRole.form.name']),
                  maxLength: MAX_NAME_LENGTH
                }
              )
            ]}
            onChange={onChangeName}
          />
          <TextValidator
            fullWidth={true}
            id="description"
            className={classes.formInput}
            label={formatMessage(messages['userDefinedRole.form.description'])}
            name="description"
            value={description}
            validators={[`maxStringLength:${MAX_DESCRIPTION_LENGTH}`]}
            errorMessages={[
              formatMessage(
                appMessages['app.maxLength'],
                {
                  fieldName: formatMessage(messages['userDefinedRole.form.description']),
                  maxLength: MAX_DESCRIPTION_LENGTH
                }
              )
            ]}
            onChange={onChangeDescription}
          />
        </FormPanel>
        <Toolbar className={classes.toolBar}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.button}
            component={MaterialUiLink}
            to="/user-defined-roles"
          >
            <CancelIcon className={clsx(classes.leftIcon, classes.iconSmall)} />
            {formatMessage(appMessages['app.cancel'])}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={classes.button}
            disabled={isFormSubmitting || !dirty}
          >
            <SaveIcon formSubmitting={isFormSubmitting} />
            {formatMessage(appMessages['app.save'])}
          </Button>
        </Toolbar>
      </ValidatorForm>
      <RouteLeavingGuard when={dirty}/>
    </>
  );
});
